import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { BulkScheduleEditDialog } from '@/components/maintenance/BulkScheduleEditDialog';
import type { BulkResult } from '@/components/common/BulkFieldEditDialog';
import { format, isPast, isWithinInterval, addDays } from 'date-fns';
import {
  Calendar,
  Clock,
  Plus,
  AlertTriangle,
  CheckCircle,
  Wrench,
  Trash2,
  Play,
  Pause,
  MoreVertical,
  Zap,
  Gauge,
  Pencil,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  PageHeader,
  StatusBadge,
  MetricCard,
  KPIGrid,
  DataTable,
  Column,
  TableFilters,
} from '@/components/common';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  maintenanceScheduleApi,
  MaintenanceSchedule,
  MaintenanceScheduleDashboard,
  MAINTENANCE_FREQUENCIES,
  MAINTENANCE_PRIORITIES,
  SCHEDULE_STATUSES,
} from '@/api/services/maintenance-schedule.api';
import { equipmentApi, Equipment } from '@/api/services/equipment.api';

export default function MaintenanceSchedules() {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([]);
  const [dashboard, setDashboard] = useState<MaintenanceScheduleDashboard | null>(null);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [frequencyFilter, setFrequencyFilter] = useState<string>('');

  // Selected ids survive a filter change but their rows do not, which would
  // leave the bar offering to edit schedules that are no longer on screen.
  useEffect(() => {
    setSelectedIds([]);
  }, [statusFilter, priorityFilter, frequencyFilter]);
  const [showOverdue, setShowOverdue] = useState(false);
  const [showDueSoon, setShowDueSoon] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<MaintenanceSchedule | null>(null);
  const [isGenerateWOOpen, setIsGenerateWOOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    equipmentId: '',
    frequency: 'MONTHLY',
    intervalDays: '',
    intervalHours: '',
    nextDue: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    priority: 'MEDIUM',
    estimatedDuration: '',
    instructions: '',
    checklistItems: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (frequencyFilter) params.frequency = frequencyFilter;
      if (showOverdue) params.overdue = true;
      if (showDueSoon) params.dueSoon = true;

      const [schedulesRes, dashboardRes, equipmentRes] = await Promise.all([
        maintenanceScheduleApi.getAll(params),
        maintenanceScheduleApi.getDashboard(),
        equipmentApi.getAll({ limit: 100 }),
      ]);

      setSchedules(schedulesRes.data.data);
      setTotalPages(schedulesRes.data.pagination.totalPages);
      setDashboard(dashboardRes.data);
      setEquipment(equipmentRes.data.data);
    } catch (error) {
      toast.error('Failed to load maintenance schedules');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, priorityFilter, frequencyFilter, showOverdue, showDueSoon]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async () => {
    try {
      const data: any = {
        name: formData.name,
        equipmentId: formData.equipmentId,
        frequency: formData.frequency,
        nextDue: formData.nextDue,
        priority: formData.priority,
      };

      if (formData.intervalDays) data.intervalDays = parseInt(formData.intervalDays);
      if (formData.intervalHours) data.intervalHours = parseFloat(formData.intervalHours);
      if (formData.estimatedDuration)
        data.estimatedDuration = parseFloat(formData.estimatedDuration);
      if (formData.instructions) data.instructions = formData.instructions;
      if (formData.checklistItems) {
        data.checklistItems = formData.checklistItems.split('\n').filter(Boolean);
      }

      await maintenanceScheduleApi.create(data);
      toast.success('Maintenance schedule created successfully');
      setIsCreateOpen(false);
      resetForm();
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create schedule');
    }
  };

  const handleStatusUpdate = async (id: string, status: string) => {
    try {
      await maintenanceScheduleApi.update(id, { status });
      toast.success('Schedule status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await maintenanceScheduleApi.delete(id);
      toast.success('Schedule deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleGenerateWorkOrder = async () => {
    if (!selectedSchedule) return;
    try {
      const result = await maintenanceScheduleApi.generateWorkOrder(selectedSchedule.id);
      toast.success(`Work order ${(result.data as any).workOrderNumber} created successfully`);
      setIsGenerateWOOpen(false);
      loadData();
      navigate(`/work-orders/${(result.data as any).id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to generate work order');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      equipmentId: '',
      frequency: 'MONTHLY',
      intervalDays: '',
      intervalHours: '',
      nextDue: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
      priority: 'MEDIUM',
      estimatedDuration: '',
      instructions: '',
      checklistItems: '',
    });
  };

  const getDueStatus = (nextDue: string) => {
    const dueDate = new Date(nextDue);
    const now = new Date();
    const sevenDaysFromNow = addDays(now, 7);

    if (isPast(dueDate)) {
      return { label: 'Overdue', color: 'bg-red-100 text-red-700' };
    }
    if (isWithinInterval(dueDate, { start: now, end: sevenDaysFromNow })) {
      return { label: 'Due Soon', color: 'bg-amber-100 text-amber-700' };
    }
    return { label: 'On Track', color: 'bg-emerald-100 text-emerald-700' };
  };

  const scheduleColumns: Column<MaintenanceSchedule>[] = [
    {
      key: 'name',
      header: 'Schedule Name',
      sortable: true,
      linkTo: (schedule) => `/maintenance/${schedule.id}`,
      render: (schedule) => (
        <div className="flex items-center gap-2 font-medium">
          {schedule.status === 'ACTIVE' && (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
              <Zap className="h-3 w-3 mr-1" />
              Auto
            </Badge>
          )}
          <span>{schedule.name}</span>
        </div>
      ),
    },
    {
      key: 'equipment',
      header: 'Equipment',
      render: (schedule) => (
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="text-sm">{schedule.equipment?.name}</div>
            <div className="text-xs text-muted-foreground">{schedule.equipment?.assetNumber}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'frequency',
      header: 'Frequency',
      sortable: true,
      render: (schedule) => (
        <>{MAINTENANCE_FREQUENCIES.find((f) => f.value === schedule.frequency)?.label}</>
      ),
    },
    {
      key: 'nextDue',
      header: 'Next Due',
      sortable: true,
      render: (schedule) => {
        const dueStatus = getDueStatus(schedule.nextDue);
        return (
          <div className="space-y-1">
            <div className="text-sm">{format(new Date(schedule.nextDue), 'MMM d, yyyy')}</div>
            <Badge variant="outline" className={dueStatus.color}>
              {dueStatus.label}
            </Badge>
          </div>
        );
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (schedule) => <StatusBadge status={schedule.priority} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (schedule) => <StatusBadge status={schedule.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-[80px]',
      render: (schedule) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setSelectedSchedule(schedule);
                  setIsGenerateWOOpen(true);
                }}
              >
                <Wrench className="mr-2 h-4 w-4" />
                Generate Work Order
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {schedule.status === 'ACTIVE' ? (
                <DropdownMenuItem onClick={() => handleStatusUpdate(schedule.id, 'PAUSED')}>
                  <Pause className="mr-2 h-4 w-4" />
                  Pause Schedule
                </DropdownMenuItem>
              ) : schedule.status === 'PAUSED' ? (
                <DropdownMenuItem onClick={() => handleStatusUpdate(schedule.id, 'ACTIVE')}>
                  <Play className="mr-2 h-4 w-4" />
                  Resume Schedule
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={() => handleDelete(schedule.id)} className="text-red-600">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <PageHeader
        breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Maintenance' }]}
        title="Maintenance Schedules"
        description="Preventive routines set against assets, with interval and the date each next falls due."
        actions={
          <Button onClick={() => navigate('/maintenance/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New Schedule
          </Button>
        }
      />

      {/* Dashboard KPI Cards */}
      {dashboard && (
        <KPIGrid columns={4}>
          <MetricCard
            label="Active Schedules"
            value={dashboard.active}
            icon={<Calendar className="h-5 w-5" />}
            sentiment="success"
          />
          <MetricCard
            label="Due This Week"
            value={dashboard.dueSoon}
            icon={<Clock className="h-5 w-5" />}
            sentiment="warning"
          />
          <MetricCard
            label="Overdue"
            value={dashboard.overdue}
            icon={<AlertTriangle className="h-5 w-5" />}
            sentiment="danger"
          />
          <MetricCard
            label="Compliance Rate"
            value={`${dashboard.total > 0 ? Math.round(((dashboard.active - dashboard.overdue) / dashboard.active) * 100) : 100}%`}
            icon={<CheckCircle className="h-5 w-5" />}
            sentiment="info"
          />
        </KPIGrid>
      )}

      {/* PM Automation Status */}
      {dashboard && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Auto-Generation Status Card */}
          <Card>
            <CardHeader>
              <h3 className="text-base font-medium text-foreground">
                <Zap className="h-5 w-5 text-blue-500" />
                PM Automation Status
              </h3>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Auto-generated this month:</span>
                <Badge variant="secondary" className="text-lg font-bold">
                  {dashboard.autoGeneratedThisMonth || 0} WOs
                </Badge>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Automation reduces manual scheduling effort by ~80%</span>
              </div>
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 text-xs">
                  <CheckCircle className="h-3 w-3 text-emerald-500" />
                  <span className="text-muted-foreground">
                    Cron jobs running: Calendar (hourly), Hours-based (30 min)
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Hours-Based PM Progress Card */}
          <Card>
            <CardHeader>
              <h3 className="text-base font-medium text-foreground">
                <Gauge className="h-5 w-5 text-amber-500" />
                Hours-Based PM Status
              </h3>
            </CardHeader>
            <CardContent>
              {!dashboard.hoursBasedStatus || dashboard.hoursBasedStatus.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No hours-based maintenance schedules configured
                </p>
              ) : (
                <div className="space-y-4 max-h-[200px] overflow-y-auto">
                  {dashboard.hoursBasedStatus.map((pm) => (
                    <div key={pm.scheduleId} className="space-y-2">
                      <div className="flex justify-between items-start text-sm">
                        <div className="flex-1">
                          <div className="font-medium">{pm.equipmentName}</div>
                          <div className="text-xs text-muted-foreground">
                            {pm.equipmentAssetNumber} • {pm.scheduleName}
                          </div>
                        </div>
                        <div className="text-right text-xs">
                          <div className="font-medium">
                            {pm.currentHours.toLocaleString()} / {pm.intervalHours.toLocaleString()}{' '}
                            hrs
                          </div>
                          <div className="text-muted-foreground">
                            {pm.hoursSinceLastPM.toLocaleString()} since last PM
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Progress
                          value={pm.percentToNextPM}
                          className={
                            pm.isOverdue
                              ? 'bg-red-100 [&>div]:bg-red-500'
                              : pm.isDueSoon
                                ? 'bg-amber-100 [&>div]:bg-amber-500'
                                : 'bg-emerald-100 [&>div]:bg-emerald-500'
                          }
                        />
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-muted-foreground">
                            {pm.percentToNextPM}% to next PM
                          </span>
                          {pm.isOverdue ? (
                            <Badge variant="destructive" className="h-5 text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Overdue
                            </Badge>
                          ) : pm.isDueSoon ? (
                            <Badge
                              variant="secondary"
                              className="h-5 text-xs bg-amber-100 text-amber-700"
                            >
                              <Clock className="h-3 w-3 mr-1" />
                              Due Soon
                            </Badge>
                          ) : (
                            <Badge
                              variant="secondary"
                              className="h-5 text-xs bg-emerald-100 text-emerald-700"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              On Track
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedules Table */}
      <DataTable<MaintenanceSchedule>
        toolbar={
          <TableFilters
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search schedules..."
            filters={[
              {
                id: 'status',
                label: 'Statuses',
                value: statusFilter,
                options: SCHEDULE_STATUSES.map((s) => ({ value: s.value, label: s.label })),
                onChange: (v) => setStatusFilter(v),
                placeholder: 'Status',
              },
              {
                // Overdue and Due Soon are mutually exclusive server-side flags,
                // which is a select rather than two toggles fighting each other.
                id: 'due',
                label: 'Due',
                value: showOverdue ? 'overdue' : showDueSoon ? 'dueSoon' : '',
                options: [
                  { value: 'overdue', label: 'Overdue' },
                  { value: 'dueSoon', label: 'Due Soon' },
                ],
                onChange: (v: string) => {
                  setShowOverdue(v === 'overdue');
                  setShowDueSoon(v === 'dueSoon');
                },
                placeholder: 'Due',
              },
              {
                id: 'priority',
                label: 'Priorities',
                value: priorityFilter,
                options: MAINTENANCE_PRIORITIES.map((p) => ({ value: p.value, label: p.label })),
                onChange: (v) => setPriorityFilter(v),
                placeholder: 'Priority',
              },
              {
                id: 'frequency',
                label: 'Frequencies',
                value: frequencyFilter,
                options: MAINTENANCE_FREQUENCIES.map((f) => ({ value: f.value, label: f.label })),
                onChange: (v) => setFrequencyFilter(v),
                placeholder: 'Frequency',
              },
            ]}
          />
        }
        data={schedules}
        columns={scheduleColumns}
        loading={loading}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(schedule) => schedule.id}
        onRowClick={(schedule) => navigate(`/maintenance/${schedule.id}`)}
        emptyTitle="No maintenance schedules found"
      />

      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl border bg-card p-3 shadow-floating">
          <span className="pl-1 text-sm font-medium">
            {selectedIds.length} schedule{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <Button size="sm" onClick={() => setBulkEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit fields
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
            <X className="mr-2 h-4 w-4" />
            Clear
          </Button>
        </div>
      )}

      <BulkScheduleEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        scheduleIds={selectedIds}
        onApplied={(result: BulkResult) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.maintenanceSchedules.lists() });
          setSelectedIds(result.results.filter((r) => r.status === 'failed').map((r) => r.id));
        }}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create Schedule Sheet */}
      <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create Maintenance Schedule</SheetTitle>
            <SheetDescription>
              Set up a new preventive maintenance schedule for equipment
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Schedule Name</Label>
              <Input
                id="name"
                placeholder="e.g., Monthly Filter Replacement"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="equipment">Equipment</Label>
              <Select
                value={formData.equipmentId}
                onValueChange={(v) => setFormData({ ...formData, equipmentId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select equipment" />
                </SelectTrigger>
                <SelectContent>
                  {equipment.map((eq) => (
                    <SelectItem key={eq.id} value={eq.id}>
                      {eq.name} ({eq.assetNumber})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="frequency">Frequency</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(v) => setFormData({ ...formData, frequency: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAINTENANCE_FREQUENCIES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(v) => setFormData({ ...formData, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAINTENANCE_PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="nextDue">Next Due Date</Label>
              <Input
                id="nextDue"
                type="date"
                value={formData.nextDue}
                onChange={(e) => setFormData({ ...formData, nextDue: e.target.value })}
              />
            </div>
            {(formData.frequency === 'HOURS_BASED' || formData.frequency === 'CONDITION_BASED') && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="intervalDays">Interval (Days)</Label>
                  <Input
                    id="intervalDays"
                    type="number"
                    placeholder="30"
                    value={formData.intervalDays}
                    onChange={(e) => setFormData({ ...formData, intervalDays: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="intervalHours">Interval (Hours)</Label>
                  <Input
                    id="intervalHours"
                    type="number"
                    placeholder="500"
                    value={formData.intervalHours}
                    onChange={(e) => setFormData({ ...formData, intervalHours: e.target.value })}
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="estimatedDuration">Estimated Duration (Hours)</Label>
              <Input
                id="estimatedDuration"
                type="number"
                step="0.5"
                placeholder="2.0"
                value={formData.estimatedDuration}
                onChange={(e) => setFormData({ ...formData, estimatedDuration: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="instructions">Instructions</Label>
              <Textarea
                id="instructions"
                placeholder="Maintenance instructions and procedures..."
                rows={3}
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="checklist">Checklist Items (one per line)</Label>
              <Textarea
                id="checklist"
                placeholder="Check oil level&#10;Replace filter&#10;Inspect belts"
                rows={4}
                value={formData.checklistItems}
                onChange={(e) => setFormData({ ...formData, checklistItems: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={!formData.name || !formData.equipmentId}>
                Create Schedule
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Generate Work Order Confirmation */}
      <Dialog open={isGenerateWOOpen} onOpenChange={setIsGenerateWOOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Work Order</DialogTitle>
            <DialogDescription>
              This will create a new work order based on the maintenance schedule &quot;
              {selectedSchedule?.name}&quot; and update the next due date.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Equipment:</span>
                <span className="font-medium">{selectedSchedule?.equipment?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Priority:</span>
                <StatusBadge status={selectedSchedule?.priority || 'MEDIUM'} />
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Required Parts:</span>
                <span className="font-medium">
                  {selectedSchedule?.requiredParts?.length || 0} items
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGenerateWOOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateWorkOrder}>Generate Work Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
